import React, {
  useCallback,
  useRef,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { StateSnapshot, Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import LoaderSpinner from '@wconcept/wconcept-ui-renewal/src/components/atoms/LoaderSpinner';
import MolocoRecProductList from '@components/pages/common/MolocoRecProductList';
import ProductList from '@wconcept/wconcept-ui-renewal/src/components/organisms/_list/ProductList';
import { useNavigation } from '@contexts/NavigationContext';
import { useShortFormPlayback } from '@wconcept/wconcept-ui-renewal/src/hooks/useShortFormPlayback';
import useIsBody from '@wconcept/wconcept-ui-renewal/src/hooks/useIsBody';
import { useSearchParams } from 'next/navigation';
import { Product } from '@wconcept/wconcept-common/models/display/product';
import useHistoryRouter from '@hooks/useLinkTo';
import MolocoRecLpProduct from '@components/common/MolocoRecLpProduct';
import ProductGridItem from './ProductGridItem';
import { VirtualizedListProps } from '../types';
import ProductObserveGridItem from './ProductObserveGridItem';

// 컴포넌트 바깥에 두어 매 렌더마다 새 함수가 만들어지지 않도록 처리
const computeItemKey = (index: number) => `group-${index}`;
function FooterSpinner() {
  return <LoaderSpinner />;
}

const VirtualizedList = React.memo<VirtualizedListProps>(
  ({
    gridItems,
    similarNode,
    mobileVirtualColumnCount = 2,
    isMobile,
    assistData,
    myHeartList,
    appInfo,
    onClickProduct,
    onClickHeart,
    onClickMolocoProductItem,
    recommendBgColor,
    hasNextPage,
    debouncedFetchNextPage,
    totalCount,
    shortFormPlayback,
    isVideoPlay,
    onClickViewStylVdClick,
    onClickMolocoAdLpProduct,
    showExclusiveEmblem,
  }: VirtualizedListProps) => {
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const { navigationStatus } = useNavigation();
    const { linkTo } = useHistoryRouter();

    useEffect(() => {
      if (navigationStatus === 'reload') {
        window.history.replaceState({}, '', undefined);
      }
    }, [navigationStatus]);

    // viewport 관찰
    const [viewportOffset, setViewportOffset] = useState({ top: 0, bottom: 0 });
    // scrollY는 effect 내부에서만 비교용으로 쓰이므로 ref로 두어
    // state 갱신으로 인한 effect 재등록 사이클을 막는다.
    const scrollYRef = useRef(0);
    const gnbTypes = useSearchParams().get('gnbType') || '';
    const { isBody } = useIsBody();

    const shortFormChildren = useMemo(
      () =>
        gridItems
          .flatMap(group => group?.map(item => item?.product))
          .filter(Boolean) as React.ReactNode[],
      [gridItems]
    );

    const {
      shouldObserve,
      activeVideoIndex,
      setActiveVideoIndex,
      visibleIndicesRef,
    } = useShortFormPlayback({
      shortFormPlayback: shortFormPlayback || false,
      isVideoPlay: isVideoPlay || false,
      children: shortFormChildren,
    });

    // viewport 관찰 계산
    useEffect(() => {
      if (!isMobile || !isBody) return;

      const calculateMargins = () => {
        const scrollYPos = window.pageYOffset;
        const scrollChange = scrollYPos - scrollYRef.current;

        // 스크롤 변화량이 1보다 작으면 무시
        if (Math.abs(scrollChange) < 1) return;
        scrollYRef.current = scrollYPos;
        const scDir = scrollChange > 0 ? 'scrollDown' : 'scrollUp';

        const header = document.getElementById('header');
        const mainGnb = document.getElementById('main-gnb-bar');
        const areaMenu = document.querySelector('#container .area-menu');
        const actionbar = document.querySelector('#actionbar .inner-actionbar');

        let topSpace = 0;
        if (header) {
          if (gnbTypes === 'Y') {
            if (scDir === 'scrollUp') {
              topSpace += header.offsetHeight;
            }
          } else {
            topSpace += header.offsetHeight;
          }
        }
        if (mainGnb) topSpace += mainGnb.offsetHeight;
        if (areaMenu) topSpace += (areaMenu as HTMLElement).offsetHeight;

        setViewportOffset({
          top: topSpace || 0,
          bottom: (actionbar as HTMLElement)?.offsetHeight || 0,
        });
      };

      // debounce 함수로 성능 최적화
      let timeoutId: NodeJS.Timeout;
      const debouncedCalculate = () => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(calculateMargins, 100);
      };

      window.addEventListener('scroll', debouncedCalculate);
      calculateMargins();

      // eslint-disable-next-line consistent-return
      return () => {
        window.removeEventListener('scroll', debouncedCalculate);
        clearTimeout(timeoutId);
        return undefined;
      };
    }, [gnbTypes, isBody, isMobile]);

    const onClickViewStylVd = useCallback(
      async (product: Product, index: number) => {
        onClickViewStylVdClick?.(product, index);
        if (product.shortFormInfo?.landingUrl) {
          linkTo.push({
            url: product.shortFormInfo?.landingUrl,
          });
        }
      },
      [linkTo, onClickViewStylVdClick]
    );

    const itemContent = useCallback(
      (index: number) => {
        const group = gridItems[index];
        if (!group || group.length === 0) return null;

        // 방금 본 상품의 추천(similar) 행
        if (group[0].type === 'similar') {
          return similarNode ?? null;
        }

        if (group[0].type === 'ad' && group[0].adData) {
          return (
            <MolocoRecProductList
              list={group[0].adData.items}
              isNativeAppBoth={appInfo.isNativeAppBoth}
              onClickItem={onClickMolocoProductItem}
              bgColor={recommendBgColor}
              showExclusiveEmblem={showExclusiveEmblem}
              onClickHeart={(adItem, isActive, adIndex) =>
                onClickHeart?.(adItem, isActive, adIndex, 'moloco')
              }
            />
          );
        }

        return (
          <>
            {group.map((item, groupIndex) => {
              if (
                item.type === 'productAd' &&
                item.molocoProduct &&
                item.originalIndex !== undefined
              ) {
                return (
                  <MolocoRecLpProduct
                    // eslint-disable-next-line react/no-array-index-key
                    key={`ad-${item.originalIndex}-${groupIndex}`}
                    molocoProduct={item.molocoProduct}
                    displayIndex={item.originalIndex}
                    isNativeAppBoth={appInfo.isNativeAppBoth}
                    onClickItem={onClickMolocoAdLpProduct}
                    showExclusiveEmblem={showExclusiveEmblem}
                    mobileVirtualColumnCount={mobileVirtualColumnCount}
                    onClickHeart={(product, isActive, originalIndex) =>
                      onClickHeart?.(product, isActive, originalIndex, 'moloco')
                    }
                  />
                );
              }

              if (
                item.type === 'product' &&
                item.product &&
                item.originalIndex !== undefined
              ) {
                const { product: rawProduct, originalIndex } = item;
                const product = rawProduct as Product;
                return shortFormPlayback ? (
                  <ProductObserveGridItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${originalIndex}-${groupIndex}`}
                    product={product}
                    originalIndex={originalIndex}
                    isMobile={!!isMobile}
                    isLogin={assistData.isLogin}
                    myHeartList={myHeartList?.product}
                    showExclusiveEmblem={showExclusiveEmblem}
                    mobileVirtualColumnCount={mobileVirtualColumnCount}
                    onClickProduct={onClickProduct}
                    onClickHeart={onClickHeart}
                    shouldObserve={shouldObserve}
                    index={originalIndex}
                    setActiveVideoIndex={setActiveVideoIndex}
                    activeVideoIndex={activeVideoIndex}
                    visibleIndices={visibleIndicesRef.current}
                    viewportOffset={viewportOffset}
                    isVideoPlay={isVideoPlay}
                    onClickViewStylVd={onClickViewStylVd}
                  />
                ) : (
                  <ProductGridItem
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${originalIndex}-${groupIndex}`}
                    product={product}
                    originalIndex={originalIndex}
                    isMobile={!!isMobile}
                    isLogin={assistData.isLogin}
                    myHeartList={myHeartList?.product}
                    showExclusiveEmblem={showExclusiveEmblem}
                    mobileVirtualColumnCount={mobileVirtualColumnCount}
                    onClickProduct={onClickProduct}
                    onClickHeart={onClickHeart}
                  />
                );
              }
              return null;
            })}
          </>
        );
      },
      [
        gridItems,
        appInfo.isNativeAppBoth,
        onClickMolocoProductItem,
        recommendBgColor,
        onClickHeart,
        shortFormPlayback,
        isMobile,
        assistData.isLogin,
        myHeartList?.product,
        onClickProduct,
        shouldObserve,
        setActiveVideoIndex,
        activeVideoIndex,
        visibleIndicesRef,
        viewportOffset,
        isVideoPlay,
        onClickViewStylVd,
        showExclusiveEmblem,
        onClickMolocoAdLpProduct,
        mobileVirtualColumnCount,
        similarNode,
      ]
    );

    const handleScroll = useCallback(() => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      // restoreStateFrom이 비활성화되어 있어 읽기 경로가 끊긴 상태이므로,
      // 일관성을 위해 쓰기 경로(history.state.virtuosoState 저장)도 함께 비활성화한다.
      // 스크롤 복원이 필요해지면 restoreStateFrom과 함께 안전하게 재도입할 것.
      // scrollTimeoutRef.current = setTimeout(() => {
      //   requestAnimationFrame(() => {
      //     virtuosoRef.current?.getState((state: StateSnapshot) => {
      //       if (state && state.scrollTop > 0) {
      //         window.history.replaceState(
      //           {
      //             ...window.history.state,
      //             virtuosoState: state,
      //           },
      //           '',
      //           undefined
      //         );
      //       }
      //     });
      //   });
      // }, 250);
    }, []);

    // 스크롤 이벤트 리스너 등록
    useEffect(() => {
      const scrollElement = window;
      scrollElement.addEventListener('scroll', handleScroll, { passive: true });

      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
    }, [handleScroll]);

    // Footer components 객체도 매 렌더마다 새 객체가 되지 않도록 메모이제이션
    const virtuosoComponents = useMemo(
      () => ({
        Footer: hasNextPage ? FooterSpinner : undefined,
      }),
      [hasNextPage]
    );

    return (
      <ProductList
        virtual
        perView={mobileVirtualColumnCount}
        mobileVirtualColumnCount={mobileVirtualColumnCount}
        shortFormPlayback={shortFormPlayback}
        isVideoPlay={isVideoPlay}
      >
        <Virtuoso
          ref={virtuosoRef}
          data={gridItems}
          itemContent={itemContent}
          computeItemKey={computeItemKey}
          endReached={debouncedFetchNextPage}
          useWindowScroll
          // restoreStateFrom={window.history.state?.virtuosoState}
          components={virtuosoComponents}
          increaseViewportBy={1600}
        />
      </ProductList>
    );
  }
);

VirtualizedList.displayName = 'VirtualizedList';

export default VirtualizedList;
